import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProgressBar from "../../../Components/ProgressBar";
import ModalV2 from "../../../Components/ModalV2";
import CancelTrip from "../CancelTrip";
import { updateClubbedTripStatus } from "../action";
import {
  EQUIPMENT_MAP,
  getProgressTemplate,
  getProgressTemplateGeneric,
  TRIP_STATUS,
} from "../../../helpers/constants";
import styles from "./index.module.css";
import ActionButtons from "./ActionButtons";
import DestinationSection from "./DestinationSection";
import EntitySection from "./EntitySection";
import { useTranslation } from "react-i18next";
import { IoWarningSharp } from "react-icons/io5";
import SwipeButton from "../../../Components/SwipeButton";
import { FaRegStickyNote } from "react-icons/fa";
import Notes from "../Notes";
import { IoIosMan } from "react-icons/io";

const PatientTransport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { id: userId = "" } = useSelector((state) => state.login.user);

  const { type: taskType = "", clubbed_trip: clubbedTrips = [] } = useSelector(
    (state) => state.home.tripDetails
  );

  const {
    id: tripId,
    destination_details: destinationDetails = [],
    accepted_shifter_ids: acceptedShifters = [],
    assigned_shifters: assignedShifters = [],
    booking_request: {
      equipment,
      entity_name: entityName,
      priority = "",
      oxygen_required: oxygenRequired,
      notes = "",
      no_of_shifters: shifterCount = 1,
    } = {},
    origin = {},
    type: tripType = "",
    status = "",
    shifter_type: shifterType = "",
  } = clubbedTrips?.[0] || {};

  const progressTemplate = useMemo(() => {
    if (equipment) return getProgressTemplate(destinationDetails);
    else return getProgressTemplateGeneric(destinationDetails);
  }, [destinationDetails]);

  const initialStep = useMemo(() => {
    const matchingStep = progressTemplate.findIndex(
      (step) => step?.prevStatus === status
    );
    return matchingStep >= 0 ? matchingStep + 1 : 1;
  }, [status, progressTemplate]);

  const [activeStep, setActiveStep] = useState(initialStep);
  const [isCancel, setCancel] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [showNotes, toggleShowNotes] = useState(false);
  const [showAreaNotes, toggleShowAreaNotes] = useState(false);
  const [areaNotes, setAreaNotes] = useState("");

  const progress = useMemo(
    () =>
      progressTemplate.map((step, index) => ({
        ...step,
        active: index + 1 === activeStep,
      })),
    [activeStep, progressTemplate]
  );

  useEffect(() => {
    setActiveStep(initialStep);
  }, [initialStep]);

  const handleUpdate = () => {
    const body = {
      destination_id:
        progress[activeStep - 1]?.prevStatus === TRIP_STATUS.STARTED_SHIFTING
          ? destinationDetails[0]?.area?.id
          : "",
      notes: "",
    };
    setLoading(true);
    dispatch(
      updateClubbedTripStatus(
        tripId,
        progress[activeStep - 1]?.status,
        body,
        () => setLoading(false)
      )
    );
  };

  const handleMultiComplete = (id = "") => {
    const body = {
      destination_id: id,
      notes: "",
    };
    setLoading(true);
    dispatch(
      updateClubbedTripStatus(
        tripId,
        progress[activeStep - 1]?.status,
        body,
        () => setLoading(false)
      )
    );
  };

  const shouldRenderGlobalButton = !(
    destinationDetails?.length > 1 &&
    activeStep === 4 &&
    destinationDetails.some((item) => item?.status === TRIP_STATUS.BOOKED)
  );

  const handleClickNotes = (notes) => {
    setAreaNotes(notes);
    toggleShowAreaNotes(true);
  };

  const handleCloseNotes = () => {
    setAreaNotes("");
    toggleShowAreaNotes(false);
  };

  return (
    <div className={styles["patient-transport"]}>
      <ProgressBar data={progress} activeStep={activeStep - 1} />

      <div className={styles["patient-transport__data"]}>
        <section className={styles["patient-transport__data-container"]}>
          {(priority === "emergency" ||
            oxygenRequired ||
            notes ||
            equipment) && (
            <div
              className={styles["patient-transport__data-container__emergency"]}
            >
              {equipment && EQUIPMENT_MAP?.[equipment]}
              {priority === "emergency" && (
                <IoWarningSharp size={18} color="#a71313" />
              )}
              {oxygenRequired && (
                <label className={styles["patient-transport__data-oxygen"]}>
                  {"O"}
                  <sub>{"2 "}</sub>
                  {t("Required")}
                </label>
              )}
              {notes && (
                <div
                  className={styles["patient-transport__data-notes"]}
                  onClick={() => toggleShowNotes(true)}
                >
                  <FaRegStickyNote color="#237fff" size={16} />
                </div>
              )}
            </div>
          )}
          <label className={styles["patient-transport__mode"]}>{`${t(
            "Mode"
          )} : ${shifterType === "primary" ? t("Main") : t("Support")}`}</label>
          {assignedShifters?.length > 1 && (
            <div className={styles["patient-transport__data-container-title"]}>
              {assignedShifters
                ?.filter((item) => item.id !== userId)
                .map((shifter, key) => (
                  <label
                    key={key}
                    className={styles["patient-transport__multi-shifter"]}
                  >
                    <IoIosMan color="#2C4861" size={16} />
                    {shifter?.name}
                  </label>
                ))}
            </div>
          )}
          <EntitySection entityName={entityName} origin={origin} />

          <DestinationSection
            destinationDetails={destinationDetails}
            activeStep={activeStep}
            handleMultiComplete={handleMultiComplete}
            handleClickNotes={handleClickNotes}
            shifterType={shifterType}
          />

          {shifterType === "primary" && (
            <ActionButtons
              status={status}
              toggleCancel={() => setCancel(true)}
            />
          )}
        </section>
      </div>

      {shifterCount !== acceptedShifters?.length ? (
        <label className={styles["patient-transport__waiting"]}>
          {t("Waiting for others")}
        </label>
      ) : (
        shouldRenderGlobalButton &&
        shifterType === "primary" && (
          <div className={styles["patient-transport__button"]}>
            <SwipeButton
              onComplete={handleUpdate}
              label={t(progress[activeStep - 1]?.buttonName)}
              loader={isLoading}
            />
          </div>
        )
      )}

      {isCancel && (
        <ModalV2 onCancel={() => setCancel(false)} type="bottom">
          <CancelTrip
            onClose={() => setCancel(false)}
            type="patient"
            tripId={tripId}
          />
        </ModalV2>
      )}
      {showNotes && (
        <ModalV2 onCancel={() => toggleShowNotes(false)} type="center">
          <Notes notes={notes} onCancel={() => toggleShowNotes(false)} />
        </ModalV2>
      )}
      {showAreaNotes && (
        <ModalV2 onCancel={handleCloseNotes} type="center">
          <Notes notes={areaNotes} onCancel={handleCloseNotes} />
        </ModalV2>
      )}
    </div>
  );
};

export default PatientTransport;
